import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import LocationSearchListContent from '../../components/search/LocationSearchListContent';
import SearchForm from '../../components/search/SearchForm/SearchForm';
import SearchMapContainer from '../../components/search/SearchMapContainer';
import SearchMapToggle from '../../components/search/SearchMapToggle';
import SearchMapListToggle from '../../components/search/SearchMapListToggle';
import {useNavigate} from 'react-router';
import {getLocationSuppliers} from '../../components/location/requests/location-requests';
import {getErrorMessageForNonStandardAndStandardResponse} from '../../util/NetworkErrorUtil';
import moment from 'moment';
import LocationSearchNoContent from '../../components/search/location-search-no-content/LocationSearchNoContent';
import '../search.css'
import {DateFormats} from "../../components/constants/securspace-constants";
import {useSnackbar} from "../../context/snackbar-context";
import {AppContext} from '../../context/app-context';
import type {Supplier} from "../../types/Supplier";
import {hasAdminAccess} from "../../util/AccountUtils";
import {useFeatureFlags} from "../../hooks/routing/useFeatureFlags";
import {LeftContainer, MainGridContainer, SearchResultsContainer} from "./styles";
import {useSearchSupplierSortBy} from "../../hooks/search/useSearchSupplierSortBy";
import {useSortBySearchParam} from "../../hooks/supplier/useSortBySearchParam";
import {useSupplierFilters} from "../../hooks/supplier/useSupplierFilters";
import {DEFAULT_INIT_LAT, DEFAULT_INIT_LNG, useDefaultLocation} from "../../hooks/maps/useDefaultLocation";
import {useGeocoder} from "../../hooks/maps/useGeocoder";
import {useLocationSearchParams} from "../../hooks/search/useLocationSearchParams";
import {usePrerenderReady} from "../../hooks/usePrerenderReady";
import {useGeocodedPlaceByParams} from "../../hooks/search/useGeocodedPlaceByParams";
import {getQueryParamsFromPacItem, getQueryParamsFromPlace, parseLatLngParams} from "../../util/AddressUtils";
import {useGeocodedPlaceByLocalStorageLatLng} from "../../hooks/search/useGeocodedPlaceByLocalStorageLatLng";
import {saveGeocodedPlaceToLocalStorage} from "../../util/PlaceStorageUtil";
import {useGeocodedPlaceByBrowserLatLng} from "../../hooks/search/useGeocodedPlaceByBrowserLatLng";
import {useGeocodedPlaceByIpLatLng} from "../../hooks/search/useGeocodedPlaceByIpLatLng";
import {useRedirectSearchByLocationParams} from "../../hooks/search/useRedirectSearchByLocationParams";
import SearchSEO from "../../components/search/SearchSEO";
import SearchResultsHeading from "../../components/search/SearchResultsHeading";
import {getCachedLocationSuppliers, shouldUseCache} from "./cache/LocationCache";
import {useDistanceBetween} from "../../hooks/maps/useDistanceBetween";
import useSupplierMapBoundaryFilter from "../../hooks/supplier/useSupplierMapBoundaryFilter";
import {PlacePrediction} from "../../components/placesAutocomplete/interfaces";
import {useSearchMapToggle} from "../../hooks/search/useSearchMapToggle";
import {getLatLng} from "../../util/SupplierUtils";

const DEFAULT_DAYS_AHEAD = 30;
const DEFAULT_NUM_SPACES = 1;
const DEFAULT_ERROR_MESSAGE = "Failed to fetch locations";

type GeocoderResult = google.maps.GeocoderResult;

const DEFAULT_RADIUS_MILES: Readonly<number> = 50;

// Debounce for writing the dragged map center to the URL — a pan writes once
// after it settles. No geocoding is involved, so this only governs (free) URL
// updates; separate from the shorter bounds-filter debounce inside the map.
const MAP_DRAG_URL_DEBOUNCE_MS = 400;

// The search box represents a searched *area*, so it must only ever display a
// locality (city, state, country). We intentionally omit any street-level
// address parts here — otherwise a precise geocode result (e.g. when city/state
// are missing from the URL and the coordinates resolve to a street address)
// would surface a street address in the search box, which should never happen.
const formatPlace = (place: GeocoderResult | null | undefined): string => {
  if (!place) return '';
  const {city, state, country} = getQueryParamsFromPlace(place, 'long_name', false) || {};
  const parts: string[] = [];
  if (city) parts.push(city);
  if (state) parts.push(state);
  if (country) parts.push(country);
  return parts.join(', ');
};

// Minimal *synthetic* GeocoderResult carrying only coordinates — used when a
// lat/lng URL reverse-geocodes to no locality (e.g. rural areas), so we still
// center and fetch suppliers at the user's explicit coordinates rather than
// redirecting away. NOTE: it has no real Google methods/fields beyond
// geometry.location and address_components — consumers must only read those.
const makePlaceFromCoords = (lat: number, lng: number): GeocoderResult => ({
  geometry: { location: new google.maps.LatLng(lat, lng) },
  address_components: [],
  formatted_address: '',
  place_id: '',
  types: [],
} as unknown as GeocoderResult);

const Search = () => {
  const {snackbarShowMessage} = useSnackbar();
  const {loading: geocoderLoading, geocoder} = useGeocoder();
  const {user} = useContext(AppContext);
  const [unfilteredLocations, setUnfilteredLocations] = useState<Array<Supplier>>([]);

  const navigate = useNavigate();
  const [currentLocationParams, setCurrentLocationParams] = useLocationSearchParams();
  // Latest URL params, read inside effects/timeouts without re-subscribing them
  // (so the drag-write timer and the place-sync effect don't loop on their own writes).
  const currentLocationParamsRef = useRef(currentLocationParams);
  currentLocationParamsRef.current = currentLocationParams;

  // Form inputs / filters
  const [instantApprovalOnly, setInstantApproval] = useState(false);
  const [onlyLiveLocations, setOnlyLiveLocations] = useState(true);
  const [showRequestLocationForm, setShowRequestLocationForm] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [selectedSort, setSelectedSort] = useSortBySearchParam('distance');

  const filterState = useMemo(() => ({
    selectedEquipmentType: selectedEquipment,
    setSelectedEquipmentType: setSelectedEquipment,
    selectedLocationFeatures: selectedFeatures,
    setSelectedLocationFeatures: setSelectedFeatures,
    selectedSort,
    setSelectedSort,
  }), [selectedEquipment, setSelectedEquipment, selectedFeatures, setSelectedFeatures, selectedSort, setSelectedSort]);

  // UI state
  const {isMapActive, toggleController} = useSearchMapToggle();
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  // True once a supplier fetch has completed at least once — gates the
  // viewport-driven form visibility so the request form can't flash before
  // suppliers have loaded.
  const [hasFetchedSuppliers, setHasFetchedSuppliers] = useState(false);
  // Crawler snapshot waits for the first supplier fetch to settle.
  usePrerenderReady(hasFetchedSuppliers && !loadingSuppliers);
  const [listHover, setListHover] = useState(null);

  // Search location state
  // Map's commanded center. Seeded from the URL viewport coords (so a reloaded
  // or shared "dragged" URL opens where the map was left), falling back to the
  // default. Only re-commanded on a new search / place resolution — never on
  // drag (drag writes the URL directly; see the drag-write effect below).
  const [mapPosition, setMapPosition] = useState<google.maps.LatLngLiteral>(
    () => parseLatLngParams(currentLocationParams) ?? {
      lat: DEFAULT_INIT_LAT,
      lng: DEFAULT_INIT_LNG
    }
  );
  const [currentPlace, setCurrentPlace] = useState<GeocoderResult>();
  // Drag (coordinate) mode: once the user drags the map, the search becomes
  // coordinate-based — the search box clears, the URL keeps only lat/lng
  // (city/state/place_id dropped), and we do NOT geocode. The request form's
  // city/state is filled by the user via autocomplete instead. Reset on a new
  // search. Ephemeral (not derived from the URL), so a reloaded coordinate URL
  // resolves a label normally.
  const [isDragMode, setIsDragMode] = useState(false);
  // True once the user has genuinely dragged the map this session — distinguishes
  // a real drag from a programmatic recenter (new search / initial load).
  const userHasDraggedRef = useRef(false);
  const handleUserDragStart = useCallback(() => {
    userHasDraggedRef.current = true;
  }, []);

  // Effective coordinate mode = dragged this session AND the URL is coords-only.
  // If the URL regains a city/state (browser back/forward to an earlier ?city=
  // entry, or a new search), we are no longer coordinate-driven even if the drag
  // flag still lingers — so the restored location resolves and shows in the box
  // instead of staying blank. Derived (not the raw flag) to avoid a reset race.
  const inCoordinateMode = isDragMode && !(currentLocationParams?.city && currentLocationParams?.state);

  // Map bounds state (managed by SearchMapContainer, exposed via callbacks)
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | undefined>();
  const [mapBoundsInitialized, setMapBoundsInitialized] = useState(false);


  const {optimizedLocationSearch, searchMapV2, cachedSupplierSearch} = useFeatureFlags();

  // Derived display value for the search input; only recomputes when currentPlace changes (not on every keystroke), preventing parent re-renders during typing.
  const formattedSearchValue = useMemo(() => {
    return formatPlace(currentPlace);
  }, [currentPlace]);

  // these are the different hooks which handle logic to get an address from somewhere to display
  // they are set up to cascade in the order that they fail, then the useRedirectSearch hook will handle redirecting to the new location
  const {geocodedAddressFromParams, geocodedAddressFromParamsLoading, geocodingAddressFromParamsFailed} = useGeocodedPlaceByParams({
    currentPlace,
    currentLocationParams,
    skip: inCoordinateMode
  });

  const {
    geocodedPlaceByLocalStorageLatLng,
    failedToGetByLocalStorage
  } = useGeocodedPlaceByLocalStorageLatLng({hasCurrentPlace: !!currentPlace, shouldTryLocalStorage: geocodingAddressFromParamsFailed});

  const {geocodedPlaceByBrowserLatLng, failedToGetByBrowserLatLng} = useGeocodedPlaceByBrowserLatLng({
    currentPlace,
    shouldTryBrowserLatLng: failedToGetByLocalStorage,
    currentLocationParams
  });
  const {geocodedPlaceByIpLatLng, failedToGetByIp} = useGeocodedPlaceByIpLatLng({
    currentPlace,
    shouldTryGeocodeByIp: failedToGetByBrowserLatLng
  })
  const {defaultLocation} = useDefaultLocation();

  // Bootstrap currentPlace from the geocoded URL params (initial load / URL-driven nav).
  // Only runs while currentPlace is unset so it never clobbers a later place transition.
  useEffect(() => {
    if (!currentPlace && geocodedAddressFromParams) {
      // URL address is valid and geocoded properly, set currentPlace state to reflect it
      setCurrentPlace({...geocodedAddressFromParams});
      // Prefer the URL viewport coords (e.g. a reloaded dragged position) over the
      // geocoded place center, so reload/share opens where the map was left.
      setMapPosition(parseLatLngParams(currentLocationParamsRef.current) ?? {
        lat: geocodedAddressFromParams.geometry.location.lat(),
        lng: geocodedAddressFromParams.geometry.location.lng()
      });
    }
  }, [currentPlace, geocodedAddressFromParams]);

  // Fallback for a valid lat/lng URL that reverse-geocodes to no locality (rural
  // areas): synthesize a coords-only place so the map centers + fetches at those
  // coordinates instead of redirecting away. Skipped in drag mode (currentPlace
  // stays frozen) and once a real place has resolved.
  useEffect(() => {
    if (currentPlace || isDragMode) return;
    if (geocodedAddressFromParams || geocodedAddressFromParamsLoading) return;
    const cur = currentLocationParamsRef.current;
    // A city/state URL (e.g. landing-page "popular cities" links) is resolved by
    // the forward-geocode cascade — never synthesize over it. That cascade's
    // loading flag can read false on the very first render, so this guard (not
    // the loading flag) is what stops the synthetic coords-only place from
    // clobbering the real place and stripping city/state from the URL.
    if (cur?.city && cur?.state) return;
    const coords = parseLatLngParams(cur);
    if (!coords) return;
    setCurrentPlace(makePlaceFromCoords(coords.lat, coords.lng));
  }, [currentPlace, isDragMode, geocodedAddressFromParams, geocodedAddressFromParamsLoading]);

  // Sync the map position + URL params from the resolved place.
  // IMPORTANT: this depends ONLY on currentPlace. If it also depended on
  // geocodedAddressFromParams (which briefly becomes undefined while a newly
  // submitted location is being geocoded), this effect would re-run with a STALE
  // currentPlace and write the old location's params back to the URL — reverting
  // the search and cancelling the in-flight place_id geocode. That is exactly why
  // searching a location with no results appeared to do nothing.
  useEffect(() => {
    if (currentPlace) {
      // Prefer the URL viewport coords (dragged position) over the place center,
      // so resolving the anchor place for the search box doesn't yank the map
      // back to the city center on reload.
      const urlViewport = parseLatLngParams(currentLocationParamsRef.current);
      setMapPosition(urlViewport ?? {
        lat: currentPlace.geometry.location.lat(),
        lng: currentPlace.geometry.location.lng(),
      });
      // Ensure URL params always reflect the current place, even if no suppliers exist.
      // Exclude street-level details (street_number, street_name) — the search page
      // represents an area search, so the URL should not carry a fabricated street
      // address from reverse-geocoding (locality fields + lat/lng remain).
      const params = getQueryParamsFromPlace(currentPlace, 'short_name', false);
      if (params) {
        // The viewport coords are owned by the map (written on drag). When the URL
        // already carries them, keep them so the anchor place can't overwrite where
        // the user panned to; otherwise seed lat/lng from the place center.
        if (urlViewport) {
          params.lat = currentLocationParamsRef.current?.lat;
          params.lng = currentLocationParamsRef.current?.lng;
        }
        setCurrentLocationParams(params);
      }
    }
  }, [currentPlace, setCurrentLocationParams]);

  // Save currentPlace to local storage whenever it changes
  useEffect(() => {
    // Only cache real, resolvable places — never a synthesized coords-only place
    // (no locality), so the local-storage fallback can't later resurrect it.
    if (currentPlace && getQueryParamsFromPlace(currentPlace)?.city) {
      saveGeocodedPlaceToLocalStorage(currentPlace);
    }
  }, [currentPlace]);


  // redirect if necessary
  useRedirectSearchByLocationParams({
    defaultLocation,
    failedToGetByBrowserLatLng,
    failedToGetByIp,
    failedToGetByLocalStorage,
    geocodedPlaceByBrowserLatLng,
    geocodedPlaceByIpLatLng,
    geocodedPlaceByLocalStorageLatLng,
    geocodingAddressFromParamsFailed: geocodingAddressFromParamsFailed,
  });

  // location filtering by map boundary (controlled)
  // Don't filter until map bounds are initialized to prevent flashing of incorrect results
  const {filteredSuppliers: mapBoundaryFilteredSuppliers} = useSupplierMapBoundaryFilter({
    unfilteredSuppliers: mapBoundsInitialized ? unfilteredLocations : [],
    bounds: mapBounds
  });

  // --- Drag → update the URL coordinates (coordinate mode) ---
  // On a genuine user drag (debounced once the map settles), write only lat/lng
  // and drop city/state/place_id. No geocoding — the search box clears and the
  // request form's city/state is user-entered via autocomplete. The supplier
  // fetch is untouched (the existing bounds filter drives the list).
  const dragUrlWriteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // Only react to a genuine user drag — a programmatic recenter (new search /
    // initial load) must not enter coordinate mode.
    if (!mapBoundsInitialized || !mapBounds || !userHasDraggedRef.current) return;
    const center = mapBounds.getCenter();
    if (!center) return;
    const lat = String(center.lat());
    const lng = String(center.lng());

    if (dragUrlWriteTimeoutRef.current) clearTimeout(dragUrlWriteTimeoutRef.current);
    dragUrlWriteTimeoutRef.current = setTimeout(() => {
      // Bail if a new search happened after this timer was scheduled (it resets
      // the flag) — otherwise a stale drag-write would clobber the search.
      if (!userHasDraggedRef.current) return;
      // Consume the armed drag: only a fresh `dragstart` re-arms it, so later
      // programmatic recenters (browser back/forward to a ?city= URL, a new
      // search) can't re-enter coordinate mode and drop city/state. The
      // "dragged this session" state lives in isDragMode, not this ref.
      userHasDraggedRef.current = false;
      const cur = currentLocationParamsRef.current;
      if (cur?.lat === lat && cur?.lng === lng) return; // no real movement
      setIsDragMode(true);
      setCurrentLocationParams({lat, lng}, {replace: true});
    }, MAP_DRAG_URL_DEBOUNCE_MS);

    return () => {
      if (dragUrlWriteTimeoutRef.current) clearTimeout(dragUrlWriteTimeoutRef.current);
    };
  }, [mapBounds, mapBoundsInitialized, setCurrentLocationParams]);

  // --- Request-a-space form follows the viewport, not just the initial fetch ---
  // Once bounds are known, show the form whenever the current map view has no
  // suppliers (geographic boundary filter — deliberately not the attribute
  // filters), so it hides as the user drags into an area with spaces and returns
  // when they drag back to an empty one.
  useEffect(() => {
    if (!mapBoundsInitialized || loadingSuppliers || !hasFetchedSuppliers) return;
    setShowRequestLocationForm(mapBoundaryFilteredSuppliers.length === 0);
  }, [mapBoundaryFilteredSuppliers, mapBoundsInitialized, loadingSuppliers, hasFetchedSuppliers]);

  // location filtering
  const {filteredLocations} = useSupplierFilters({
    unfilteredLocations: mapBoundaryFilteredSuppliers,
    instantApprovalOnly,
    user,
    onlyLiveLocations,
    selectedFeatures,
    selectedEquipment
  });

  // If using cached suppliers, pass the unfiltered suppliers to add the distance field
  // Otherwise this hook's logic will not run and will not edit unfilteredLocations because the distance field already exists
  useDistanceBetween({
    coordinates: cachedSupplierSearch ? unfilteredLocations : [],
    setCoordinates: cachedSupplierSearch ? setUnfilteredLocations : () => {
    },
    center: cachedSupplierSearch ? mapPosition : null,
    getLatLng
  });

  // location sorting
  const {sortedSuppliers} = useSearchSupplierSortBy({searchSortBy: selectedSort, unsortedSuppliers: filteredLocations});

  useEffect(() => {
    let cancelled = false;

    const getDateRange = (numDaysAhead: number) => {
      const today = moment();
      const formattedStartDate = today.format(DateFormats.DAY);

      const futureDate = today.add(numDaysAhead, 'day');
      const formattedEndDate = futureDate.format(DateFormats.DAY);

      return [formattedStartDate, formattedEndDate];
    };

    const fetchSuppliers = async (location: {
      lat: number,
      lng: number
    }, startDate: string, endDate: string, numberOfSpaces: number) => {
      setLoadingSuppliers(true);
      const shouldFetchFromCache = cachedSupplierSearch && shouldUseCache(window.location.hostname);
      const fetchSuppliersFromApi = async (): Promise<Array<Supplier>> => {
        const response = await getLocationSuppliers(
          optimizedLocationSearch,
          location.lat.toString(),
          location.lng.toString(),
          startDate,
          endDate,
          numberOfSpaces
        );

        return response.body;
      };

      try {
        let suppliers: Array<Supplier>;

        if (shouldFetchFromCache) {
          try {
            const response = await getCachedLocationSuppliers(user, window.location.hostname);
            suppliers = response.body.locations.map((loc: any) => ({
              ...loc,
              visible: loc.active,
              locationId: loc.id,
              addressLatitude: loc.lat,
              addressLongitude: loc.lon,
              hasInstantApproval: loc.hasInstantApproval ?? false,
            }));
          } catch (cacheError: unknown) {
            if (cancelled) return;

            console.error('[Search] Cached supplier fetch failed; falling back to API.', {
              error: cacheError,
              hostname: window.location.hostname,
              cachedSupplierSearch,
              optimizedLocationSearch,
            });

            suppliers = await fetchSuppliersFromApi();
          }
        } else {
          suppliers = await fetchSuppliersFromApi();
        }

        if (cancelled) return;

        const noSuppliers = !suppliers.length;
        setShowRequestLocationForm(noSuppliers);
        setUnfilteredLocations(suppliers);

      } catch (e: any) {
        if (cancelled) return;

        console.error('[Search] Supplier fetch failed.', {
          error: e,
          hostname: window.location.hostname,
          shouldFetchFromCache,
          cachedSupplierSearch,
          optimizedLocationSearch,
        });

        snackbarShowMessage(`${DEFAULT_ERROR_MESSAGE}: ${getErrorMessageForNonStandardAndStandardResponse(e, DEFAULT_ERROR_MESSAGE)}`, 'error', 15000);
      } finally {
        if (!cancelled) {
          setLoadingSuppliers(false);
          setHasFetchedSuppliers(true);
        }
      }
    }

    if (currentPlace) {
      const [startDate, endDate] = getDateRange(DEFAULT_DAYS_AHEAD);
      fetchSuppliers({
        lat: currentPlace.geometry.location.lat(),
        lng: currentPlace.geometry.location.lng()
      }, startDate, endDate, DEFAULT_NUM_SPACES);
    }

    return () => {
      cancelled = true;
    };
  }, [currentPlace, optimizedLocationSearch, cachedSupplierSearch, snackbarShowMessage, user, navigate]);

  // if cached, unfiltered locations exist, and at least one location is within the default radius, hide the form
  useEffect(() => {
    // Pre-bounds heuristic only: once map bounds are known, the viewport effect
    // above owns form visibility (so dragging hides/shows the form correctly).
    if (mapBoundsInitialized) return;
    if (cachedSupplierSearch && shouldUseCache(window.location.hostname) && unfilteredLocations.length) {
      setShowRequestLocationForm(!unfilteredLocations.some(loc => Number(loc.distance) <= DEFAULT_RADIUS_MILES));
    }
  }, [unfilteredLocations, filteredLocations, cachedSupplierSearch, mapBoundsInitialized]);

  const handleSearchSubmit = useCallback((pacItem: PlacePrediction | undefined) => {
    if (!pacItem?.place_id || geocoderLoading) return;

    const params = getQueryParamsFromPacItem(pacItem)

    // A new explicit search exits coordinate mode and re-anchors on the searched city.
    userHasDraggedRef.current = false;
    setIsDragMode(false);
    // Geocoder provides the place, so that is why we set currentPlace from here.
    setCurrentLocationParams(params);
  }, [geocoderLoading, setCurrentLocationParams])

  useEffect(() => {
    if (currentLocationParams?.place_id && !geocoderLoading && geocoder.geocode) {
      let isActive = true;
      const place_id = currentLocationParams.place_id

      geocoder.geocode({placeId: place_id}, (places: GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        // Guard against stale callback after unmount or dependency change
        if (!isActive) return;

        // Check status first for better error logging
        if (status !== google.maps.GeocoderStatus.OK) {
          console.error('Geocoding failed for place_id:', place_id, 'Status:', status);
          return;
        }

        // Now validate places when status is OK
        if (!places || places.length === 0) {
          console.error('No place results from geocode for place id:', place_id, '(status was OK)');
          return;
        }

        // Always take the first result from geocoding since we are geocoding a specific place_id which should only return one result
        const locationLatLng = places[0].geometry.location || null;
        if (!locationLatLng) {
          navigate('/search');
        } else {
          setCurrentPlace(places[0]);
          setMapPosition({lat: locationLatLng.lat(), lng: locationLatLng.lng()});
        }
      });

      return () => {
        isActive = false;
      };
    }

  }, [currentLocationParams, geocoder, navigate, geocoderLoading]);


  const handleSelectLocation = useCallback((location: any) => {
    const queryParams = new URLSearchParams();
    if (location.hasInstantApproval && instantApprovalOnly) {
      queryParams.append("showMonth", String(false));
    }
    const newLink = `location-profile/${location.locationId}?${queryParams.toString()}`;
    window.open(newLink, "_blank");
  }, [instantApprovalOnly]);


  const showAdminOnlyOptions = hasAdminAccess(user);

  // Initial city/state for the request form. Empty in coordinate (drag) mode so
  // the user fills it via the form's city autocomplete; otherwise the searched
  // locality pre-fills it.
  const resolvedPlaceParams = useMemo(() => getQueryParamsFromPlace(currentPlace), [currentPlace]);
  const requestFormCity = inCoordinateMode ? '' : (resolvedPlaceParams?.city || currentLocationParams?.city || '');
  const requestFormState = inCoordinateMode ? '' : (resolvedPlaceParams?.state || currentLocationParams?.state || '');

  // The search box clears in coordinate (drag) mode; otherwise it shows the
  // searched place.
  const searchBoxValue = inCoordinateMode ? '' : formattedSearchValue;

  return (
    <>
      <SearchSEO locationParams={currentLocationParams} locations={sortedSuppliers}/>
      <MainGridContainer container>
        <SearchResultsContainer container item direction="row">
          <LeftContainer item isMapActive={isMapActive}>
            <SearchForm
              onSearchSubmit={handleSearchSubmit}
              onInstantApprovalChange={setInstantApproval}
              onLiveLocationsChange={setOnlyLiveLocations}
              searchValue={searchBoxValue}
              instantApprovalOnly={instantApprovalOnly}
              onlyLiveLocations={onlyLiveLocations}
              showAdminOnlyOptions={showAdminOnlyOptions}
              filterState={filterState}
              hideFilterControls={showRequestLocationForm}
            />
            {
              !showRequestLocationForm && currentLocationParams?.city && currentLocationParams?.state &&
                <SearchResultsHeading city={currentLocationParams.city} state={currentLocationParams.state} />
            }
            {
              showRequestLocationForm ?
                <LocationSearchNoContent city={requestFormCity} state={requestFormState} editable={!(requestFormCity && requestFormState)} onCitySearch={handleSearchSubmit}/>
                :
                <LocationSearchListContent
                  locations={sortedSuppliers}
                  loading={loadingSuppliers || (!mapBoundsInitialized && unfilteredLocations.length > 0)}
                  instantApproval={instantApprovalOnly}
                  onlyLiveLocations={onlyLiveLocations}
                  listHover={listHover}
                  setListHover={setListHover}
                />
            }
          </LeftContainer>

          <SearchMapToggle isMapActive={isMapActive}>
            <SearchMapContainer
              locations={sortedSuppliers}
              listHover={listHover}
              onLocationSelect={handleSelectLocation}
              mapCenter={mapPosition}
              useMapV2={searchMapV2}
              onBoundsChanged={setMapBounds}
              onBoundsInitialized={setMapBoundsInitialized}
              onUserDragStart={handleUserDragStart}
            />
          </SearchMapToggle>
          <SearchMapListToggle isMapActive={isMapActive} onToggle={toggleController}
                               showRequestLocationForm={showRequestLocationForm}/>
        </SearchResultsContainer>
      </MainGridContainer>
    </>
  );
};

export default Search;
